import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { telecmiApi } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone,
  PhoneOff,
  Forward,
  ExternalLink,
  Headphones,
  Sparkles,
  MessageCircle,
  Star,
  Repeat,
  Globe,
  Maximize2,
  X,
} from "lucide-react";
import Customer360Panel from "./Customer360Panel";
import { useLiveCall } from "../contexts/LiveCallContext";

/**
 * Floating popup that appears when an inbound call is ringing for the
 * current user. Polls /api/telecmi/active-incoming every 5s. When a call
 * is detected, slides in the customer 360 + AI predicted reason + last
 * 3 messages so the agent picks up *informed*.
 *
 * Audio is handled by the agent's desktop softphone (PIOPIY doesn't
 * provide a browser SDK). The "Answer" button is purely a CRM hint —
 * the actual pickup happens in the softphone.
 */
const IncomingCallPopup = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const { startCall } = useLiveCall();
  const [call, setCall] = useState(null);
  const [c360, setC360] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [showFullPanel, setShowFullPanel] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Poll for ringing calls
  useEffect(() => {
    if (!user) return;
    const tick = async () => {
      try {
        const r = await telecmiApi.activeIncoming();
        const incoming = r.data?.incoming || [];
        const fresh = incoming.find(c => !dismissed.has(c.id || c.cmiuuid));
        if (fresh) {
          if (!call || (call.id !== fresh.id && call.cmiuuid !== fresh.cmiuuid)) {
            setCall(fresh);
            // load full customer 360 in background
            const phone = fresh.from_number || fresh.to_number;
            if (phone) {
              try {
                const cr = await telecmiApi.customer360(phone);
                setC360(cr.data);
              } catch {}
            }
            // play soft ringtone (browser may block — best effort)
            try { audioRef.current?.play().catch(() => {}); } catch {}
          }
        } else if (call) {
          setCall(null); setC360(null);
        }
      } catch (e) {
        // 401 / network — silently retry
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 5000);
    return () => clearInterval(intervalRef.current);
  }, [user, dismissed, call]);

  const dismiss = () => {
    if (call?.id || call?.cmiuuid) {
      setDismissed(s => new Set([...s, call.id || call.cmiuuid]));
    }
    setCall(null); setC360(null);
  };

  const openChat = () => {
    if (c360?.lead?.id) {
      nav(`/chats?lead=${c360.lead.id}`);
    } else {
      nav("/chats");
    }
    dismiss();
  };

  if (!call) return null;

  const phone = call.from_number || call.to_number || "—";
  const customer = call.customer || {};
  const data = c360 || {};
  const name = data.name || customer.name || "Unknown caller";
  const lastMessages = data.last_messages || customer.last_messages || [];
  const predicted = data.predicted_reason;
  const lastAgent = data.last_agent;
  const initials = (name || "?").charAt(0).toUpperCase();

  return (
    <>
      <audio ref={audioRef} loop>
        <source src="data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEAQB8AAEAfAAABAAgAAABmYWN0BAAAAAAAAABkYXRhAAAAAA==" type="audio/wav" />
      </audio>
      <div
        className="fixed bottom-5 right-5 z-[100] w-[380px] bg-white rounded-3xl shadow-2xl border-2 border-emerald-300 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        data-testid="incoming-call-popup"
      >
        {/* Top stripe */}
        <div className="px-4 py-2.5 bg-emerald-50 flex items-center justify-between border-b border-emerald-100">
          <div className="flex items-center gap-2">
            <span className="relative flex w-3 h-3">
              <span className="absolute inline-flex w-3 h-3 rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex w-3 h-3 rounded-full bg-emerald-500"></span>
            </span>
            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Incoming Call · Ringing</span>
          </div>
          <button onClick={dismiss} className="text-emerald-700 hover:bg-emerald-100 rounded p-1" data-testid="incoming-popup-close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="p-4">
          {/* Customer header */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white flex items-center justify-center text-lg font-extrabold shadow-md">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base leading-tight truncate" data-testid="incoming-popup-name">{name}</h3>
              <p className="text-[11px] text-gray-500 font-mono">{phone}</p>
            </div>
          </div>

          {/* Badges */}
          {(data.vip || data.repeat || data.language) && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {data.vip && (
                <span className="text-[9px] bg-yellow-50 text-yellow-700 border border-yellow-200 font-bold px-2 py-0.5 rounded-full uppercase flex items-center gap-1">
                  <Star className="w-2.5 h-2.5 fill-yellow-500 stroke-yellow-500" /> VIP
                </span>
              )}
              {data.repeat && (
                <span className="text-[9px] bg-purple-50 text-purple-700 border border-purple-200 font-bold px-2 py-0.5 rounded-full uppercase flex items-center gap-1">
                  <Repeat className="w-2.5 h-2.5" /> Repeat
                </span>
              )}
              {data.language && (
                <span className="text-[9px] bg-blue-50 text-blue-700 border border-blue-200 font-bold px-2 py-0.5 rounded-full uppercase flex items-center gap-1">
                  <Globe className="w-2.5 h-2.5" /> {data.language}
                </span>
              )}
            </div>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <p className="text-base font-extrabold text-orange-600">{data.orders?.length || 0}</p>
              <p className="text-[8px] text-gray-500 uppercase font-bold">Orders</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <p className="text-base font-extrabold text-emerald-600">{data.ltv ? `₹${(data.ltv / 1000).toFixed(1)}k` : "—"}</p>
              <p className="text-[8px] text-gray-500 uppercase font-bold">LTV</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <p className="text-base font-extrabold text-purple-600 truncate">{lastAgent?.name?.split(" ")[0] || "—"}</p>
              <p className="text-[8px] text-gray-500 uppercase font-bold">Last agent</p>
            </div>
          </div>

          {/* Last messages */}
          {lastMessages.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-2.5 mb-3">
              <p className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <MessageCircle className="w-2.5 h-2.5" /> Last WhatsApp messages
              </p>
              <div className="space-y-1 text-[11px]">
                {lastMessages.slice(-3).map((m, i) => (
                  <p key={m.id || m.wati_id || `${m.timestamp || ""}-${i}`} className="truncate">
                    <span className={`font-bold ${m.direction === "incoming" ? "text-gray-700" : "text-orange-600"}`}>
                      {m.direction === "incoming" ? "Customer:" : "Agent:"}
                    </span>{" "}
                    <span className="text-gray-700">{m.content}</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* AI predicted reason */}
          {predicted && predicted.reason && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-2.5 mb-3">
              <p className="text-[8px] font-bold text-purple-700 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> AI predicted reason
              </p>
              <p className="text-[12px] text-purple-900 font-bold">
                {predicted.reason}
                {predicted.confidence ? <span className="text-[10px] text-purple-600 font-normal ml-1">· {predicted.confidence}% confidence</span> : null}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-3 gap-1.5">
            <button
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-[11px] py-2.5 rounded-xl flex items-center justify-center gap-1 shadow"
              onClick={async () => {
                try {
                  const r = await telecmiApi.liveStart({
                    phone: phone || call?.from_number,
                    lead_id: c360?.lead?.id,
                    direction: "inbound",
                    mock: true,  // until Telecmi auth is fixed
                  });
                  startCall({
                    id: r.data.id,
                    phone: r.data.phone,
                    name: c360?.name || phone,
                  });
                  // Open Customer 360 alongside
                  setShowFullPanel(true);
                  // Hide the ringing popup (call is now active)
                  dismiss();
                  toast.success("Call active — pick up on your softphone");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not start live session");
                }
              }}
              data-testid="incoming-popup-answer"
            >
              <Phone className="w-3.5 h-3.5" /> Answer
            </button>
            <button
              className="bg-red-500 hover:bg-red-600 text-white font-bold text-[11px] py-2.5 rounded-xl flex items-center justify-center gap-1"
              onClick={dismiss}
              data-testid="incoming-popup-decline"
            >
              <PhoneOff className="w-3.5 h-3.5" /> Decline
            </button>
            <button
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-[11px] py-2.5 rounded-xl flex items-center justify-center gap-1 border border-blue-200"
              onClick={() => toast.message("Transfer flow opens after pickup")}
              data-testid="incoming-popup-transfer"
            >
              <Forward className="w-3.5 h-3.5" /> Transfer
            </button>
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-[10px]">
            <button onClick={openChat} className="text-orange-600 hover:underline font-bold flex items-center gap-1" data-testid="incoming-popup-open-chat">
              <ExternalLink className="w-2.5 h-2.5" /> Open chat
            </button>
            <button
              onClick={() => setShowFullPanel(true)}
              className="text-purple-700 hover:underline font-bold flex items-center gap-1"
              data-testid="incoming-popup-open-360"
            >
              <Maximize2 className="w-2.5 h-2.5" /> Customer 360
            </button>
            <span className="text-gray-400 flex items-center gap-1">
              <Headphones className="w-2.5 h-2.5" /> Listen-only available
            </span>
          </div>
        </div>
      </div>
      {showFullPanel && phone && (
        <Customer360Panel phone={phone} onClose={() => setShowFullPanel(false)} mode="drawer" />
      )}
    </>
  );
};

export default IncomingCallPopup;
